/**
 * The records a conversation's cards show: a change's detail (its ops carry the drafts it wrote), the model's recap
 * of a change waiting for an OK, and an email draft's row.
 *
 * The cards used to fetch these only once they were on screen, a step behind the conversation: the reply painted,
 * then a beat later the drafts and approvals popped in under it. Now the session cache warms them as soon as it
 * has a conversation's log (Home's prefetch included), the first open waits briefly for them, and a card that
 * mounts paints from here, then checks for changes unless what it has was fetched a moment ago.
 * Module-level, small LRUs, like the session cache.
 */
import { agentApi, type Changeset, type ChangesetDigest } from './api'
import { artifactFromTarget, artifactsFromOps, type ArtifactRef, type ChangesetOp } from './artifacts'
import { initialState, reduceAll, type ChangesetState } from './reducer'
import type { AnyAgentEvent } from './protocol/types'
import { draftStore, type DraftRecord } from './components/workspace/viewers'

export type ChangesetDetail = Changeset & { ops?: ChangesetOp[] }
export interface ChangesetRecap { recap: string | null; digest?: ChangesetDigest | null }

interface Entry<T> { value: T; at: number }

/** Fetched this recently: a card that mounts uses it as is instead of asking again. */
export const FRESH_MS = 10_000
const MAX_ENTRIES = 60

function lru<T>() {
    const entries = new Map<string, Entry<T>>()
    const inflight = new Map<string, Promise<T>>()
    function peek(key: string): Entry<T> | null {
        const e = entries.get(key)
        if (!e) return null
        entries.delete(key)
        entries.set(key, e)
        return e
    }
    function set(key: string, value: T): void {
        entries.delete(key)
        entries.set(key, { value, at: Date.now() })
        while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value as string)
    }
    /** One request per key at a time; an entry younger than `maxAge` answers without one. */
    function get(key: string, fetch: () => Promise<T>, maxAge = 0): Promise<T> {
        const e = entries.get(key)
        if (e && maxAge > 0 && Date.now() - e.at < maxAge) return Promise.resolve(e.value)
        const running = inflight.get(key)
        if (running) return running
        const p = Promise.resolve()
            .then(fetch)
            .then((v) => { set(key, v); return v })
            .finally(() => inflight.delete(key))
        inflight.set(key, p)
        return p
    }
    /** Drop an entry fetched before `t` (the record has changed since). */
    function forgetBefore(key: string, t: number): void {
        const e = entries.get(key)
        if (e && e.at < t) entries.delete(key)
    }
    return { peek, set, get, forgetBefore, clear: () => { entries.clear(); inflight.clear() } }
}

// A change's detail depends on where it stands (applying it writes the ops' records), so it's kept per state.
const changesets = lru<ChangesetDetail>()
const recaps = lru<ChangesetRecap | null>()
const drafts = lru<DraftRecord | null>()
/** Each conversation's warm-up, so an open can wait on the one a prefetch started. */
const warming = new Map<string, Promise<void>>()

const csKey = (id: string, state: ChangesetState) => `${id}:${state}`

export function peekChangeset(id: string, state: ChangesetState): Entry<ChangesetDetail> | null {
    return changesets.peek(csKey(id, state))
}
export function loadChangeset(id: string, state: ChangesetState, maxAge = 0): Promise<ChangesetDetail> {
    return changesets.get(csKey(id, state), async () => {
        const t = Date.now()
        const detail = await agentApi().getChangeset(id, true) as ChangesetDetail
        // A draft this change wrote (a revision keeps the draft's id) is newer than a row cached before it.
        for (const a of singleDrafts(artifactsFromOps(detail.ops))) drafts.forgetBefore(a.id, t)
        return detail
    }, maxAge)
}

const singleDrafts = (list: (ArtifactRef | null)[]) => list.filter((a): a is ArtifactRef => a?.type === 'draft' && !a.items)

/** A recap once written doesn't change; a null one (nothing to say, or it failed) is asked again next time. */
export function peekRecap(id: string): ChangesetRecap | null {
    return recaps.peek(id)?.value ?? null
}
export function loadRecap(id: string): Promise<ChangesetRecap | null> {
    const known = peekRecap(id)
    if (known?.recap) return Promise.resolve(known)
    return recaps.get(id, async () => {
        const r = await agentApi().getChangesetRecap(id)
        return r ? { recap: r.recap ?? null, digest: r.digest ?? null } : null
    })
}

/** Null: not known here. A known entry's value is null when the draft is gone. */
export function peekDraft(id: string): Entry<DraftRecord | null> | null {
    return drafts.peek(id)
}
export function loadDraft(id: string, maxAge = 0): Promise<DraftRecord | null> {
    return drafts.get(id, () => draftStore().load(id), maxAge)
}

/**
 * Fetch what this conversation's cards will need: every change it shows (not the agent's own unshown drafts), the
 * recap of one waiting for an OK, and the drafts it wrote or opened. Resolves (never rejects) once what decides the
 * cards' layout is in; a recap or a change's own draft rows may still be on the way.
 */
export function warmRecords(sessionId: string, events: AnyAgentEvent[]): Promise<void> {
    const running = warming.get(sessionId)
    if (running) return running
    let jobs: Promise<unknown>[] = []
    try {
        const st = reduceAll(initialState(sessionId), structuredClone(events))
        for (const cs of Object.values(st.changesets)) {
            if (cs.state === 'draft') continue
            const detail = loadChangeset(cs.id, cs.state, FRESH_MS)
            jobs.push(detail)
            // An applied change's drafts carry their fields, but only the row says one was sent since. Not waited on:
            // the card is already in place, only its Sent label would change.
            if (cs.state === 'applied') {
                detail.then((d) => singleDrafts(artifactsFromOps(d.ops)).forEach((a) => { loadDraft(a.id, FRESH_MS).catch(() => {}) }))
                    .catch(() => {})
            }
            // A model writes it (slow when not yet written): warmed, never waited on; the card cross-fades to it.
            if (cs.state === 'pending_approval') loadRecap(cs.id).catch(() => {})
        }
        for (const a of singleDrafts(st.actions.map((x) => artifactFromTarget(x.target)))) jobs.push(loadDraft(a.id, FRESH_MS))
    } catch {
        jobs = []
    }
    const p = Promise.allSettled(jobs).then(() => undefined).finally(() => warming.delete(sessionId))
    warming.set(sessionId, p)
    return p
}

/** The warm-up under way for this conversation, if any. */
export function recordsWarming(sessionId: string): Promise<void> | null {
    return warming.get(sessionId) ?? null
}

export function clearRecordCache(): void {
    changesets.clear()
    recaps.clear()
    drafts.clear()
    warming.clear()
}
